import dayjs, { Dayjs } from 'dayjs';
import { flatten } from 'lodash';

import { getFormFieldData } from '../../common/filterHelpers';
import { getEnumKeyByEnumValue } from '../../common/helpers';
import { SearchFormFieldEnum } from '../../common/searchFormField.types';
import { ContractTypeEnum, LanguageLevelEnum, LanguageType, LevelEnum } from '../../common/types';
import { MONTH_PICKER_PAYLOAD_FORMAT } from '../../components/forms/FormMonthPicker';
import { CandidateType } from '../candidates/Candidates.types';

type CandidateFormValues = Record<string, unknown> & {
  contractTypes?: ContractTypeEnum[];
  languages?: LanguageType[];
  professionalExperienceSince?: Dayjs | null;
  recruiter?: number;
  [SearchFormFieldEnum.HrInterviewDate]?: Dayjs;
  [SearchFormFieldEnum.TechnicalInterviewDate]?: Dayjs;
};

const candidateEditableFieldNames = [
  'firstName',
  'lastName',
  'linkedinProfile',
  'recruiter',
  'email',
  'phoneNumber',
  'appliedPosition',
  'githubProfile',
  'additionalLink',
  'city',
  'expectedSalary',
  'totalEmploymentCost',
  'noticePeriod',
  'summary',
  'recommendedBy',
  'source',
  'professionalExperienceSince',
  'seniority',
  'technologies',
  'languages',
  'createdDate',
  'createdBy',
  'workMode',
  SearchFormFieldEnum.ContractTypes,
  SearchFormFieldEnum.HrInterviewDate,
  'hrInterviewAssessment',
  'hrInterviewSummary',
  SearchFormFieldEnum.TechnicalInterviewDate,
  'technicalInterviewAssessment',
  'technicalInterviewSummary',
] as const;

const normalizeComparableValue = (value: unknown): unknown => {
  if (dayjs.isDayjs(value)) {
    return value.valueOf();
  }

  if (Array.isArray(value)) {
    return value.map(normalizeComparableValue);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, entryValue]) => {
      acc[key] = normalizeComparableValue(entryValue);
      return acc;
    }, {});
  }

  return value ?? null;
};

export const getComparableCandidateFormValues = (values: Record<string, unknown> = {}) =>
  candidateEditableFieldNames.reduce<Record<string, unknown>>((acc, fieldName) => {
    acc[fieldName] = normalizeComparableValue(values[fieldName]);
    return acc;
  }, {});

const mapEnumValueToFormKey = <T extends { [index: string]: string }>(enumValues: T, value?: string | null) => {
  if (!value) {
    return value;
  }

  return (getEnumKeyByEnumValue(enumValues, value) as string | null) ?? value;
};

export const normalizeCandidateLanguages = (languages?: LanguageType[]): LanguageType[] =>
  (languages ?? []).map((language) => ({
    language: language.language,
    level: mapEnumValueToFormKey(LanguageLevelEnum, String(language.level)) ?? String(language.level),
  }));

export const mapProfessionalExperienceSinceToFormValue = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) {
    return null;
  }

  const parsedValue = dayjs(value, MONTH_PICKER_PAYLOAD_FORMAT);
  return parsedValue.isValid() && parsedValue.format(MONTH_PICKER_PAYLOAD_FORMAT) === value ? parsedValue : null;
};

export const mapProfessionalExperienceSinceToPayloadValue = (value?: Dayjs | string | null) => {
  if (!value) {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.format(MONTH_PICKER_PAYLOAD_FORMAT) : null;
  }

  const normalizedValue = value.trim();
  return normalizedValue || null;
};

export const buildCandidateFormPayload = (values: CandidateFormValues, cv?: string) => {
  const restValues = { ...values };
  delete restValues.cv;

  const hrInterviewDate = restValues[SearchFormFieldEnum.HrInterviewDate]
    ? {
        hrInterviewDate: dayjs(restValues[SearchFormFieldEnum.HrInterviewDate] as Dayjs).unix(),
      }
    : {};

  const technicalInterviewDate = restValues[SearchFormFieldEnum.TechnicalInterviewDate]
    ? {
        technicalInterviewDate: dayjs(restValues[SearchFormFieldEnum.TechnicalInterviewDate] as Dayjs).unix(),
      }
    : {};

  return {
    ...restValues,
    [SearchFormFieldEnum.ContractTypes]:
      getFormFieldData(SearchFormFieldEnum.ContractTypes).toPayloadValueFormatter?.(
        restValues[SearchFormFieldEnum.ContractTypes] as ContractTypeEnum[]
      ) ?? [],
    languages: normalizeCandidateLanguages(restValues.languages),
    professionalExperienceSince: mapProfessionalExperienceSinceToPayloadValue(restValues.professionalExperienceSince),
    ...hrInterviewDate,
    ...technicalInterviewDate,
    ...(cv !== undefined ? { cv } : {}),
  };
};

export const mapCandidateDetailsToFormValues = (candidate: CandidateType) => {
  const hrInterviewDate = candidate[SearchFormFieldEnum.HrInterviewDate]
    ? {
        hrInterviewDate: dayjs.unix(candidate[SearchFormFieldEnum.HrInterviewDate]),
      }
    : {};

  const technicalInterviewDate = candidate[SearchFormFieldEnum.TechnicalInterviewDate]
    ? {
        technicalInterviewDate: dayjs.unix(candidate[SearchFormFieldEnum.TechnicalInterviewDate]),
      }
    : {};

  return {
    ...candidate,
    contractTypes: getFormFieldData(SearchFormFieldEnum.ContractTypes).toFormValueFormatter?.(
      flatten([candidate.contractTypes]) as string[]
    ),
    languages: normalizeCandidateLanguages(candidate.languages),
    professionalExperienceSince: mapProfessionalExperienceSinceToFormValue(candidate.professionalExperienceSince),
    seniority: mapEnumValueToFormKey(LevelEnum, candidate.seniority),
    ...hrInterviewDate,
    ...technicalInterviewDate,
    createdDate: dayjs.unix(candidate.createdDate).format('YYYY-MM-DD'),
    createdBy: `${candidate.createdBy.firstName} ${candidate.createdBy.lastName}`,
    recruiter: candidate.recruiter?.id,
  };
};
